import { loadRemote } from '@module-federation/runtime';
import React from 'react';

type RemoteComponentModule<P extends object> = {
  default: React.ComponentType<P>;
};

function hasDefaultExport<P extends object>(
  value: unknown
): value is RemoteComponentModule<P> {
  return typeof value === 'object' && value !== null && 'default' in value;
}

/**
 * A remote's exposed module as a lazy component. Pass a prop type (e.g.
 * `lazyRemote<{ onNavigateToCase: (caseId: string) => void }>(...)`) when the
 * host needs to pass props — e.g. a navigation callback — into the remote.
 *
 * `loadRemote` is typed as `unknown` — the module is resolved at runtime from
 * `window.__RUNTIME_CONFIG__`, so there is nothing for the compiler to check
 * against. Narrowing here rather than asserting at each call site keeps the
 * one unavoidable runtime check in one place, and gives a named failure
 * instead of a `default is not a constructor` deep inside React.
 */
export function lazyRemote<P extends object = Record<string, never>>(
  module: string
): React.LazyExoticComponent<React.ComponentType<P>> {
  return React.lazy(async () => {
    const remoteModule = await loadRemote(module);

    if (!hasDefaultExport<P>(remoteModule)) {
      throw new Error(`Remote module "${module}" has no default export`);
    }

    return { default: remoteModule.default };
  });
}
